import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NgxSpinnerService } from 'ngx-spinner';
import { EMPTY, of } from 'rxjs';
import { catchError, delay, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ReservationApiService } from 'src/app/apis/reservation-api.service';
import { TablesApiService } from 'src/app/apis/tables-api.service';
import { LocalStorageService } from 'src/app/lib/local-storage.service';
import { ReservationService } from 'src/app/lib/reservation.service';
import { UtilsService } from 'src/app/lib/utils.service';
import { Reservation } from 'src/app/model/reservation';
import { environment } from 'src/environments/environment';
import * as AppActions from '../actions/app.actions';
import * as ReservationListActions from '../actions/reservation-list.actions';
import * as ReservationActions from '../actions/reservation.actions';
import * as AppSelectors from '../selectors/app.selectors';
import * as ReservationListSelectors from '../selectors/reservation-list.selectors';
import * as ReservationSelectors from '../selectors/reservation.selectors';
import { Location } from '@angular/common';
import moment from 'moment';

@Injectable()
export class ReservationEffects {

  loadReservationFromList$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationListActions.loadReservationsSuccess, ReservationListActions.setEditingReservationId),
      withLatestFrom(
        this.store.select(ReservationListSelectors.selectAllReservations),
        this.store.select(ReservationListSelectors.selectEditingReservationId),
        this.store.select(ReservationSelectors.selectReservation)
      ),
      switchMap(([action, allReservations, editingReservationId, editingReservation]) => {

        if (editingReservationId == null) {
          return EMPTY;
        }

        // if the editingReservation exists and has the same reservationId, do not update, otherwise changes will be overwritten 
        if (action.type === ReservationListActions.loadReservationsSuccess.type
          && editingReservation != null
          && editingReservation.reservationDetails.reservationId === editingReservationId) {
          return EMPTY;
        }

        const reservation = allReservations.find(reservation => reservation.reservationDetails.reservationId === editingReservationId
          || reservation.reservationDetails.tmpId === editingReservationId);

        return of({}).pipe(
          map(data => {
            return ReservationActions.setReservation({ reservation });
          }))
      }
      )
    );
  });

  loadReservationTablesAvailability$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(
        ReservationActions.loadTablesAvailability,
        //ReservationListActions.setEditingReservationId,
        // ReservationActions.setReservation,
        // ReservationActions.setReservationCovers,
        // ReservationActions.setReservationArrivalDate,
        // ReservationActions.setReservationArrivalTime,
        // ReservationActions.setReservationReturnTime
      ),
      withLatestFrom(this.store.select(ReservationSelectors.selectReservation), this.store.select(ReservationListSelectors.selectDate)),
      switchMap(([action, reservation, date]) => {
        if (reservation == null || !this.utilsService.canWrite('LoadTablesPlan')) {
          return EMPTY;
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        const hasTimeSet = reservation?.reservationDetails?.arrivalTime?.length > 11;
        if ((reservation?.reservationDetails?.covers ?? 0) == 0 || !hasTimeSet) {
          if (action.ignoreUnsetArrivalTimeAndCovers) {
            reservation = JSON.parse(JSON.stringify(reservation)) as Reservation;
            reservation.reservationDetails.arrivalTime = moment(date).format('yyyy-MM-DD hh:mm:ss.s');
            reservation.reservationDetails.covers = 1;
          } else {
            return EMPTY;
          }
        }

        reservation = JSON.parse(JSON.stringify(reservation)) as Reservation;
        this.reservationService.cleanUpExtraneousFields(reservation);

        this.showLoader(true);

        return this.tablesApiService.getTablesAvailability(parseInt(restaurantId), reservation.reservationDetails)
          .pipe(
            map(data => {
              setTimeout(() => {
                this.hideLoader();
              }, 250);
              const tableSections = data.sectionsPocket.sort((a, b) => a.sectionOrder > b.sectionOrder ? 1 : -1);
              for (let tableSection of tableSections) {
                tableSection.sectionTables.sort((a, b) => (a.tableOrder ?? 0) > (b.tableOrder ?? 0) ? 1 : -1);
              }

              return ReservationActions.loadTablesAvailabilitySuccess({
                tableSections: data.sectionsPocket.sort((a, b) => a.sectionOrder > b.sectionOrder ? 1 : -1)
              });
            }),
            catchError(error => {
              setTimeout(() => {
                this.hideLoader();
              }, 250);
              return of(ReservationActions.loadTablesAvailabilityFailure({ error }));
            }))
      }
      )
    );
  });

  loadReservationHistory$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationActions.loadReservationHistory),
      withLatestFrom(this.store.select(ReservationListSelectors.selectEditingReservationId)),
      switchMap(([action, reservationId]) => {

        if (reservationId == null) {
          return EMPTY;
        }

        this.showLoader(true);

        return this.reservationApiService.getAuditTrails(reservationId)
          .pipe(
            map(data => {

              data.auditTrails.sort((a, b) => {
                var t1 = parseInt(a.timeperformed ?? '');
                var t2 = parseInt(b.timeperformed ?? '');

                if (t1 < t2)
                  return 1;

                if (t1 > t2)
                  return -1

                return 0;
              })

              this.utilsService.fillAuditTrailsDisplayedDiffs(data.auditTrails);

              setTimeout(() => {
                this.hideLoader();
              }, 250);

              return ReservationActions.loadReservationHistorySuccess({
                auditTrails: data.auditTrails
              });
            }),
            catchError(error => {

              setTimeout(() => {
                this.hideLoader();
              }, 250);

              return of(ReservationActions.loadReservationHistoryFailure({ error }));
            }))
      }
      )
    );
  });

  submitReservation$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationActions.submitReservation),
      withLatestFrom(this.store.select(ReservationSelectors.selectReservation), this.store.select(AppSelectors.selectIsOffline)),
      switchMap(([action, reservationToSubmit, isOffline]) => {

        if (isOffline === true && reservationToSubmit != null) {
          return of({}).pipe(
            map(data => {
              return ReservationListActions.addPendingReservation({ reservation: reservationToSubmit });
            }))
        }

        if (reservationToSubmit == null) {
          return EMPTY;
        }

        const restaurantId = this.localStorageService.getRestaurantId();

        if (restaurantId == null) {
          console.error('Restaurant Id does not exist!'); // todo: maybe redirect to the login page
          return EMPTY;
        }

        this.store.dispatch({ type: ReservationActions.setIsSubmiting.type, isSubmiting: true });

        const reservation: Reservation = JSON.parse(JSON.stringify(reservationToSubmit)); // make object mutable
        this.reservationService.prepareForSave(reservation);

        this.showLoader();

        return this.reservationApiService.createOrUpdateReservation(parseInt(restaurantId), reservation)
          .pipe(
            map(data => {
              this.hideLoader();
              this.store.dispatch({ type: ReservationActions.setIsSubmiting.type, isSubmiting: false });
              if (data?.ErrorCode != null && data?.ErrorCode.length > 0) {
                return ReservationActions.submitReservationFailure(data?.ErrorCode);
              }
              return ReservationActions.submitReservationSuccess({ reservation: this.reservationService.initReservation(reservation) });
            }),
            catchError(error => {
              this.hideLoader();
              this.store.dispatch({ type: ReservationActions.setIsSubmiting.type, isSubmiting: false });
              return of(ReservationActions.submitReservationFailure({ error }));
            }))
      }
      )
    );
  });

  submitReservationSuccess$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationActions.submitReservationSuccess),
      withLatestFrom(this.store.select(ReservationSelectors.selectReservation)),
      switchMap(([action, reservationToSubmit]) => {

        if (reservationToSubmit == null) {
          return EMPTY;
        }

        this.goBack();

        const reloadSources = reservationToSubmit.sourceDetails?.id == null || reservationToSubmit.sourceDetails?.id == 'NEW';

        if (reloadSources) {
          // optimization (15/06/2024)
          // this.store.dispatch({ type: AppActions.loadSources.type });
        } else {
          this.store.dispatch({ type: AppActions.updateSource.type, source: reservationToSubmit.sourceDetails });
        }

        if (reservationToSubmit.reservationDetails.reservationId != null) {
          return EMPTY;
        }

        return of({}).pipe(
          map(data => {
            return ReservationListActions.loadReservations();
          }))
      }
      )
    );
  });

  submitReservationFailure$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationActions.submitReservationFailure),
      withLatestFrom(this.store.select(ReservationSelectors.selectReservation), this.store.select(AppSelectors.selectIsOffline)),
      switchMap(([action, reservationToSubmit, isOffline]) => {

        if (reservationToSubmit == null) {
          return EMPTY;
        }

        this.goBack();

        if (isOffline === true && reservationToSubmit != null) {
          return of({}).pipe(
            map(data => {
              return ReservationListActions.addPendingReservation({ reservation: reservationToSubmit });
            }))
        }

        return EMPTY;
      }
      )
    );
  });

  changeRestaurant$ = createEffect(() => {
    return this.actions$.pipe(

      ofType(ReservationActions.changeRestaurant),
      switchMap((action) => {

        return this.reservationApiService.changeRestaurant(action.reservation, action.restaurantId)
          .pipe(
            map(data => {
              this.hideLoader();
              return ReservationActions.changeRestaurantSuccess();
            }),
            catchError(error => {
              this.hideLoader();
              return of(ReservationActions.changeRestaurantFailure({ error }));
            }))
      }
      )
    );
  });

  savePaymentLinkExpirationAfterSubmit$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReservationActions.submitReservationSuccess),
      withLatestFrom(
        this.store.select(ReservationSelectors.selectPaymentLinkExpiresDirty),
        this.store.select(ReservationSelectors.selectPaymentLinkExpiresAt)
      ),
      switchMap(([action, dirty, expiresAt]) => {
        if (!dirty) return EMPTY;

        const reservationId = action?.reservation?.reservationDetails?.reservationId;
        if (!reservationId || reservationId.trim().length === 0) {
          // reservation saved but no id? just clear dirty to prevent loops
          return of(ReservationActions.setPaymentLinkExpirationDirty({ dirty: false }));
        }

        // Store value is YYYY-MM-DD; backend accepts ISO-like "YYYY-MM-DDT00:00:00" (or null)
        const paymentLinkExpiresAt =
          expiresAt != null && expiresAt.trim().length > 0
            ? `${expiresAt}T00:00:00`
            : null;

        return this.reservationApiService.setPaymentLinkExpiration({
          reservationId,
          paymentLinkExpiresAt
        }).pipe(
          map(resp => {
            if (resp?.Status === true) {
              return ReservationActions.setPaymentLinkExpirationDirty({ dirty: false });
            }

            const msg =
              resp?.ErrorDescription?.trim()?.length
                ? `Payment link expiration was not saved: ${resp.ErrorDescription}`
                : 'Payment link expiration was not saved.';

            // clear dirty to avoid repeated attempts; reservation is already saved
            this.store.dispatch(AppActions.showAlert({ alert: msg }));
            return ReservationActions.setPaymentLinkExpirationDirty({ dirty: false });
          }),
          catchError(() => {
            this.store.dispatch(AppActions.showAlert({ alert: 'Payment link expiration was not saved (network error).' }));
            return of(ReservationActions.setPaymentLinkExpirationDirty({ dirty: false }));
          })
        );
      })
    );
  });

  goBack() {

    if (this.router.url.indexOf('/reservations/copy/') >= 0) {
      this.router.navigate(['/']);
      return;
    }

    if (this.router.url === '/' || this.router.url === '/reservations' || this.router.url.startsWith('/tables-plan')) {
      return;
    }
    this.location.back();
  }

  constructor(
    private actions$: Actions,
    private store: Store<{ date: Date }>,
    private tablesApiService: TablesApiService,
    private reservationApiService: ReservationApiService,
    private reservationService: ReservationService,
    private utilsService: UtilsService,
    private localStorageService: LocalStorageService,
    private spinner: NgxSpinnerService,
    private router: Router,
    private location: Location,
    private route: ActivatedRoute,
  ) { }

  showLoader(noDelay: boolean = false) {

    if (noDelay === true) {
      this.spinner.show();
      return;
    }

    this.loadingSpinnerTimeoutId = setTimeout(() => {
      this.spinner.show();
    }, environment.requestOverlayThreashold);
  }

  hideLoader() {
    if (this.loadingSpinnerTimeoutId != null) {
      clearTimeout(this.loadingSpinnerTimeoutId);
    }

    this.spinner.hide();
  }

  loadingSpinnerTimeoutId: null | ReturnType<typeof setTimeout> = null;

}
